import React, { useCallback } from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';

import { Modal } from 'ui/base/modal';
import { BooleanStore } from 'ui/base/input';
import { ButtonClickEvent } from 'ui/base/types';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page';

import { LinkButton } from 'ui/public/button';
import { CheckboxInput } from 'ui/standard/input/checkbox_input';

import {
  Direction,
  getFullDirection,
  getShortDirection,
  getMicroDirection,
} from 'assets/enums/directions/directions';

import { DirectionStore } from './direction_store';
import baseStyles from './direction_input.module.css';
import DirectionInputMessages from './direction_input_messages';

// Style declarations and themes
type DirectionInputStyles = {
  box: string;
  placeholder: string;
  modalContainer: string;
  header: string;
  commit: string;
  toggleAll: string;
  inputRow: string;
  inputLabel: string;
  inputLabelSymbol: string;
  inputLabelText: string;
};

const themedStyles: ThemedStyles<DirectionInputStyles> = {
  [Theme.DAYLIGHT]: {
    box: baseStyles.daylightBox,
    placeholder: baseStyles.daylightPlaceholder,
    modalContainer: baseStyles.modalContainer,
    header: baseStyles.header,
    commit: baseStyles.daylightCommit,
    toggleAll: baseStyles.daylightToggleAll,
    inputRow: baseStyles.inputRow,
    inputLabel: baseStyles.inputLabel,
    inputLabelSymbol: baseStyles.inputLabelSymbol,
    inputLabelText: baseStyles.inputLabelText,
  },
  [Theme.MIDNIGHT]: {
    box: baseStyles.midnightBox,
    placeholder: baseStyles.midnightPlaceholder,
    modalContainer: baseStyles.modalContainer,
    header: baseStyles.header,
    commit: baseStyles.midnightCommit,
    toggleAll: baseStyles.midnightToggleAll,
    inputRow: baseStyles.inputRow,
    inputLabel: baseStyles.inputLabel,
    inputLabelSymbol: baseStyles.inputLabelSymbol,
    inputLabelText: baseStyles.inputLabelText,
  },
};

// A component to render a row in the modal
type DirectionInputRowProps = {
  symbol: string;
  text: string;
  store: BooleanStore;
  styles: DirectionInputStyles;
};

const DirectionInputRow = observer(({ symbol, text, store, styles }: DirectionInputRowProps) => {
  const toggleStore = useCallback(action(() => {
    store.value = !store.value;
  }), [store]);

  return (
    <div className={styles.inputRow}>
      <div className={styles.inputLabel} onClick={toggleStore}>
        <div className={styles.inputLabelSymbol}>
          {symbol}
        </div>
        <div className={styles.inputLabelText}>
          {text}
        </div>
      </div>
      <CheckboxInput store={store}/>
    </div>
  );
});

// The component that handles the actual modal
type DirectionInputModalProps = {
  store: DirectionStore;
};

const DirectionInputModal = observer(({ store }: DirectionInputModalProps) => {
  const styles = useStyles(themedStyles);

  const onCommit = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.commitWorkspace();
    store.modal.hideModal();
  }), [store]);

  // Note: toggleAll functionality can be abstracted away into a component that
  // takes a list of boolean stores as argument. We can do that once a uniform
  // 'styling' for this feature has been decided.
  const onToggleAll = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    const toCheckAll = !store.workspaceHasChecked;
    Object.values(store.workspace).forEach((booleanStore) => {
      booleanStore.value = toCheckAll;
    });
  }), [store]);

  return (
    <div className={styles.modalContainer}>
      <StandardPage>
        <div className={styles.header}>
          <BackButton onClick={store.modal.hideModal}/>
          <button type='button' className={styles.commit} onClick={onCommit}>
            {DirectionInputMessages.ok()}
          </button>
        </div>
        <div className={styles.inputRow}>
          <LinkButton type='button' className={styles.toggleAll} onClick={onToggleAll}>
            {!store.workspaceHasChecked ? DirectionInputMessages.checkAll() : DirectionInputMessages.uncheckAll()}
          </LinkButton>
        </div>
        <DirectionInputRow
          styles={styles}
          symbol={getMicroDirection(Direction.OUTBOUND)}
          text={getShortDirection(Direction.OUTBOUND)}
          store={store.workspace[Direction.OUTBOUND]}
        />
        <DirectionInputRow
          styles={styles}
          symbol={getMicroDirection(Direction.INBOUND)}
          text={getShortDirection(Direction.INBOUND)}
          store={store.workspace[Direction.INBOUND]}
        />
        <DirectionInputRow
          styles={styles}
          symbol={getMicroDirection(Direction.LOCAL)}
          text={getShortDirection(Direction.LOCAL)}
          store={store.workspace[Direction.LOCAL]}
        />
      </StandardPage>
    </div>
  );
});

// The component that handles the actual modal
type DirectionInputBoxProps = {
  placeholder: string;
  store: DirectionStore;
};

const DirectionInputBox = observer(({ placeholder, store }: DirectionInputBoxProps) => {
  const styles = useStyles(themedStyles);
  const onShowModal = React.useCallback(action(() => {
    store.reloadWorkspace();
    store.modal.showModal();
  }), [store]);

  if (store.arrayValue.length > 0) {
    return (
      <button type='button' className={styles.box} onClick={onShowModal}>
        {store.arrayValue.map((dir) => getFullDirection(dir)).join(', ')}
      </button>
    );
  } else {
    return (
      <button type='button' className={styles.box} onClick={onShowModal}>
        <span className={styles.placeholder}>{placeholder}</span>
      </button>
    );
  }
});

export type DirectionInputProps = {
  placeholder: string;
  store: DirectionStore;
};

export const DirectionInput = observer(({ placeholder, store }: DirectionInputProps) => {
  return (
    <>
      <Modal store={store.modal}>
        <DirectionInputModal store={store}/>
      </Modal>
      <DirectionInputBox placeholder={placeholder} store={store}/>
    </>
  );
});
